import { API, graphqlOperation } from "aws-amplify";

import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import DeviceForm from "components/devices/DeviceForm";
import {
  createAlertDevice,
  createDeviceAction,
  createDeviceVariable,
  deleteAlertDevice,
  deleteDeviceAction,
  deleteDeviceVariable,
  updateDevice,
  updateDeviceAction,
  updateDeviceVariable,
} from "graphql/mutations";
import { Loader } from "@aws-amplify/ui-react";
import { alertDevicesByAlertId, listActions } from "graphql/queries";
import { customGetDevice } from "graphql/custom-queries";
import LoaderFullScreen from "components/utils/LoaderFullScreen";

const DeviceUpdateForm = ({ deviceId, onSuccess }) => {
  const navigate = useNavigate();
  const [device, setDevice] = useState();
  const [loading, setLoading] = useState(false);

  async function onSubmit(values) {
    try {
      const {
        companyID,
        branchID,
        name,
        location,
        brand,
        description,
        variables: newVariables,
        oldVariables,
        actions: newActions,
        alerts,
        oldActions,
        coordsLat,
        coordsLng,
        timezone,
        energyCost,
        currency,
        autoRefreshTime,
        env,
      } = values;

      setLoading(true);

      const promises = [];

      // Update Variables
      const variablesToDelete = oldVariables?.filter(
        (oldVar) =>
          !newVariables?.some(
            (newVar) => oldVar.variableID === newVar.variableID && !!newVar.id
          )
      );
      variablesToDelete?.forEach(async (variable) => {
        promises.push(
          API.graphql({
            query: deleteDeviceVariable.replaceAll("__typename", ""),
            variables: {
              input: {
                id: variable.id,
              },
            },
          })
        );
      });

      const variablesToCreate = newVariables?.filter((newVar) => !newVar.id);
      variablesToCreate?.forEach(async (variable) => {
        promises.push(
          API.graphql({
            query: createDeviceVariable.replaceAll("__typename", ""),
            variables: {
              input: {
                ...variable,
                deviceID: deviceId,
              },
            },
          })
        );
      });

      const variablesToUpdate = newVariables?.filter((newVar) => !!newVar.id);
      variablesToUpdate?.forEach(async (variable) => {
        promises.push(
          API.graphql({
            query: updateDeviceVariable.replaceAll("__typename", ""),
            variables: {
              input: {
                id: variable.id,
                calibrationFactor: variable.calibrationFactor,
              },
            },
          })
        );
      });

      // Update Actions
      const actionsToDelete = oldActions?.filter(
        (oldAct) =>
          !newActions?.some(
            (newAct) => oldAct.actionID === newAct.actionID && !!newAct.id
          )
      );
      actionsToDelete?.forEach(async (action) => {
        promises.push(
          API.graphql({
            query: deleteDeviceAction.replaceAll("__typename", ""),
            variables: {
              input: {
                id: action.id,
              },
            },
          })
        );
      });

      const actionsToCreate = newActions?.filter((newAct) => !newAct.id);
      actionsToCreate?.forEach(async (action) => {
        promises.push(
          API.graphql({
            query: createDeviceAction.replaceAll("__typename", ""),
            variables: {
              input: {
                ...action,
                deviceID: deviceId,
              },
            },
          })
        );
      });

      const actionsToUpdate = newActions?.filter((newAct) => !!newAct.id);
      actionsToUpdate?.forEach(async (action) => {
        promises.push(
          API.graphql({
            query: updateDeviceAction.replaceAll("__typename", ""),
            variables: {
              input: {
                id: action.id,
              },
            },
          })
        );
      });

      // Update alerts

      const alertsToUnLink = device.alerts
        ?.filter(
          (deviceUs) => !alerts?.some((alert) => alert.id === deviceUs.id)
        )
        ?.map((alert) => alert.id);
      alertsToUnLink?.forEach(async (alertId) => {
        const records = (
          await API.graphql({
            query: alertDevicesByAlertId.replaceAll("__typename", ""),
            variables: {
              alertId,
              filter: {
                and: [{ deviceId: { eq: deviceId } }],
              },
            },
          })
        )?.data?.alertDevicesByAlertId?.items;
        promises.push(
          API.graphql({
            query: deleteAlertDevice.replaceAll("__typename", ""),
            variables: {
              input: {
                id: records[0].id,
              },
            },
          })
        );
      });
      const alertsToLink = alerts
        ?.filter((alert) => !device.alerts?.some((rec) => rec.id === alert.id))
        ?.map((alert) => alert.id);
      alertsToLink?.forEach((alertId) => {
        promises.push(
          API.graphql({
            query: createAlertDevice.replaceAll("__typename", ""),
            variables: {
              input: {
                deviceId,
                alertId,
              },
            },
          })
        );
      });

      const deviceUpdated = (
        await API.graphql({
          query: updateDevice.replaceAll("__typename", ""),
          variables: {
            input: {
              id: deviceId,
              companyID,
              branchID,
              name,
              location,
              brand,
              description,
              coordsLat,
              coordsLng,
              timezone,
              energyCost,
              currency,
              autoRefreshTime,
              env,
            },
          },
        })
      )?.data?.updateDevice;

      await Promise.all(promises);

      if (actionsToCreate?.length > 0 || actionsToDelete?.length > 0) {
        const actionsObj = JSON.parse(deviceUpdated.actionsObj ?? "{}");

        if (actionsToCreate?.length > 0) {
          const actionsData = await API.graphql(
            graphqlOperation(listActions, {
              filter: {
                or: actionsToCreate.map((act) => ({
                  id: { eq: act.actionID },
                })),
              },
            })
          );
          const actionsItems = actionsData.data.listActions.items ?? [];
          actionsItems.forEach((actIt) => {
            const val = actIt.values && JSON.parse(actIt.values)?.[0]?.value;
            if (val) actionsObj[actIt.shortName] = val;
          });
        }

        if (actionsToDelete?.length > 0) {
          const actionsData = await API.graphql(
            graphqlOperation(listActions, {
              filter: {
                or: actionsToDelete.map((act) => ({
                  id: { eq: act.actionID },
                })),
              },
            })
          );
          const actionsItems = actionsData.data.listActions.items ?? [];
          actionsItems.forEach((actIt) => {
            if (actionsObj.hasOwnProperty(actIt.shortName))
              delete actionsObj[actIt.shortName];
          });
        }
        await API.graphql({
          query: updateDevice.replaceAll("__typename", ""),
          variables: {
            input: {
              id: deviceId,
              actionsObj: JSON.stringify(actionsObj),
            },
          },
        });
      }

      toast.success(strings.successUpdated);
      if (onSuccess) onSuccess();
      else navigate("/devices");
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDevice(id) {
    try {
      const deviceData = await API.graphql(
        graphqlOperation(customGetDevice, { id })
      );
      const deviceItem = deviceData.data.getDevice;
      if (!deviceItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/devices");
        return;
      }
      setDevice({
        ...deviceItem,
        alerts: deviceItem.alerts?.items?.map((alertDev) => ({
          id: alertDev.alert.id,
          name: alertDev.alert.name,
        })),
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  useEffect(() => {
    fetchDevice(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  if (!device) return <Loader />;

  return (
    <div>
      <DeviceForm onSubmit={onSubmit} type="update" initialValues={device} />
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Actualizar Dispositivo",
    successUpdated: "Dispositivo actualizado con éxito",
    responses: {
      errorFetch: "Error al consultar el dispositivo",
    },
  },
});

export default DeviceUpdateForm;
